/**
 * Utilities for exporting data as JSON or CSV and triggering browser downloads.
 */

// ---------------------------------------------------------------------------
// CSV helpers
// ---------------------------------------------------------------------------

/**
 * Escapes a single CSV field per RFC 4180: wraps in double-quotes when the
 * value contains a comma, double-quote, or newline, and escapes internal
 * double-quotes by doubling them (`"` -> `""`). Null/undefined become empty string.
 *
 * @param value - The raw field value to escape.
 * @returns The escaped string, suitable for inclusion in a CSV row.
 */
function escapeCSVField(value: unknown): string {
  const str = value == null ? '' : String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Serialises a 2-D data array into a CSV string with a header row.
 *
 * @param headers - Column header labels for the first row.
 * @param rows - Data rows, where each inner array corresponds to one record.
 * @returns A complete CSV string including the header line.
 */
function toCSV(headers: string[], rows: unknown[][]): string {
  const headerLine = headers.map(escapeCSVField).join(',');
  const dataLines = rows.map((row) => row.map(escapeCSVField).join(','));
  return [headerLine, ...dataLines].join('\n');
}

// ---------------------------------------------------------------------------
// Download helper
// ---------------------------------------------------------------------------

/**
 * Triggers a browser file download for the given string content.
 *
 * Creates a temporary Blob URL, clicks a hidden `<a>` element to start the
 * download, then immediately removes the element and revokes the object URL to
 * avoid memory leaks.
 *
 * @param content - The string content to download.
 * @param filename - The suggested filename for the downloaded file.
 * @param mimeType - The MIME type for the Blob (e.g. `'text/csv'`, `'application/json'`).
 */
export function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url); // free the Blob URL immediately after use
}

// ---------------------------------------------------------------------------
// Item export types & functions
// ---------------------------------------------------------------------------

/**
 * Subset of item fields included in an export.
 * `location` maps to the DB column `location_label` (renamed for readability).
 * Photo URLs and internal IDs are intentionally excluded.
 */
export interface ExportableItem {
  name: string;
  category: string | null;
  condition: string;
  status: string;
  brand: string | null;
  location: string | null;
  circulation_mode: string;
  tags: string[];
  description: string | null;
}

const ITEM_HEADERS = [
  'Name',
  'Category',
  'Condition',
  'Status',
  'Brand',
  'Location',
  'Circulation Mode',
  'Tags',
  'Description',
];

/**
 * Serialises items to a CSV string with a descriptive header row.
 *
 * @param items - The array of exportable item records.
 * @returns A CSV-formatted string ready for download.
 */
export function exportItemsAsCSV(items: ExportableItem[]): string {
  const rows = items.map((i) => [
    i.name,
    i.category,
    i.condition,
    i.status,
    i.brand,
    i.location,
    i.circulation_mode,
    i.tags.join('; '),
    i.description,
  ]);
  return toCSV(ITEM_HEADERS, rows);
}

/**
 * Serialises items to a pretty-printed JSON string.
 *
 * @param items - The array of exportable item records.
 * @returns A JSON string with 2-space indentation.
 */
export function exportItemsAsJSON(items: ExportableItem[]): string {
  return JSON.stringify(items, null, 2);
}

// ---------------------------------------------------------------------------
// Loan history export types & functions
// ---------------------------------------------------------------------------

/**
 * Subset of loan fields included in an export.
 * `duration_days` and `overdue_days` are **derived** — calculated from
 * timestamps in `useExportData.ts` and not stored in the database.
 * `borrower` and `lender` are resolved display names, not user IDs.
 */
export interface ExportableLoan {
  item_name: string;
  borrower: string | null;
  lender: string | null;
  borrowed_at: string;
  returned_at: string | null;
  due_at: string | null;
  /** Elapsed days from borrow to return (or now for active loans); minimum 1. */
  duration_days: number;
  /** Days past the due date at return time (or now for active overdue loans); null if on-time or no due date. */
  overdue_days: number | null;
  status: string;
  note: string | null;
}

const LOAN_HEADERS = [
  'Item Name',
  'Borrower',
  'Lender',
  'Borrowed At',
  'Returned At',
  'Due At',
  'Duration (days)',
  'Overdue (days)',
  'Status',
  'Note',
];

/**
 * Serialises loan history to a CSV string with a descriptive header row.
 *
 * @param loans - The array of exportable loan records.
 * @returns A CSV-formatted string ready for download.
 */
export function exportLoanHistoryAsCSV(loans: ExportableLoan[]): string {
  const rows = loans.map((l) => [
    l.item_name,
    l.borrower,
    l.lender,
    l.borrowed_at,
    l.returned_at,
    l.due_at,
    l.duration_days,
    l.overdue_days,
    l.status,
    l.note,
  ]);
  return toCSV(LOAN_HEADERS, rows);
}

/**
 * Serialises loan history to a pretty-printed JSON string.
 *
 * @param loans - The array of exportable loan records.
 * @returns A JSON string with 2-space indentation.
 */
export function exportLoanHistoryAsJSON(loans: ExportableLoan[]): string {
  return JSON.stringify(loans, null, 2);
}
